'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var global = require('global');
var videojs = _interopDefault(require('video.js'));

/**
 * Default options for the plugin.
 *
 * @namespace
 * @property {boolean} hideTranscript
 *       Whether or not the transcript is open on load.
 * @property {string} trackKind
 *       Track type to use to build transcript.  Should be a legal <track> kind value.
 * @property {string} appendContainer
 *       ID for a DOM element that the widget will be appended to upon completion.
 * @property {string} appendButton
 *       ID for a DOM element that the "show transcript" button will be appended to
 *       upon completion of the widget.  Allows the button and the widget to live in
 *       different parts of the DOM.
 * @property {int} offset
 *       A pixel value of how far extra the transcript container's scroll needs to be
 *       set when scrolling active cues.  When restyling the container, if you find the
 *       cues to be scrolling too far, use a higher number. 30 is the default for the
 *       styling provided.
 */
const defaults = {
  hideTranscript: false,
  trackKind: 'captions',
  appendContainer: 'vjs-ts',
  appendButton: 'vjs-ts',
  offset: 30
};

/**
 * Polyfill: String.includes() is not supported in IE
 */

if (!String.prototype.includes) {
  String.prototype.includes = function() { // eslint-disable-line no-extend-native
    'use strict';
    return String.prototype.indexOf.apply(this, arguments) !== -1;
  };
}

/**
 * Function to pick the default track on a video.
 *
 * This function assumes that one of the following is true:
 * - a track exists of the chosen type and has default="true" set on it
 * - page language has been set
 *
 * If neither of these are true we have no way to select a proper track to use.
 *
 * Returns an object representing the chosen text track.
 *
 * @function determineDefaultTrack
 *
 * @param {Player} player
 *       A Video.js player.
 * @param {string} trackKind
 *       A string representing the chosen track kind to use from options (captions,
 *       description, etc.).
 * @return {Object|boolean} Text track object if we can find a track of that kind,
 * else false
 */
const determineDefaultTrack = (player, trackKind) => {
  for (let i = 0; i < player.remoteTextTracks().length; i++) {
    const textTrack = player.remoteTextTracks()[i];

    if (textTrack.kind === trackKind && textTrack.default === true) {
      return textTrack;
    } else if (textTrack.kind === trackKind &&
            textTrack.language === global.document.documentElement.lang) {
      // Use the page's language as the "default" language if a default track
      // language isn't defined
      return textTrack;
    } else if (textTrack.kind === trackKind &&
            global.document.documentElement.lang.includes(textTrack.language) ||
            textTrack.language.includes(global.document.documentElement.lang)) {
      // Page language might be something like "en-GB" and we have "en" captions,
      // which are close enough and we should use them
      return textTrack;
    }

    // Couldn't find a track - return false to show "transcript unavailable"
    return false;
  }
};

/**
 * Normalizes cues to remove spaces and newlines.
 *
 * @function parseCueContent
 * @param {Object} cue
 *       The cue object to parse.
 * @return {string} Parsed cue content
 */
const parseCueContent = (cue) => {
  const captionTagRegex = '(?:<c\..+>)(.*)(?:<\/c>)';
  const captionTagReObj = new RegExp(captionTagRegex, 'g');
  let parsedCue = '';
  let cueParts = [];

  if (cue.text.match(/\n/)) {
    cueParts = cue.text.split('\n');
  } else {
    cueParts[0] = cue.text;
  }

  // capture result
  cueParts.forEach(function(value, index) {
    const results = captionTagReObj.exec(value);

    if (results && results[1]) {
      // spaces for padding to make the cues less visually ugly
      // and also deal with newlines
      parsedCue += ` ${results[1]}`;
    } else {
      parsedCue += ` ${value}`;
    }

    // reset state
    captionTagReObj.lastIndex = 0;
  });

  return parsedCue;
};

/**
 * Function to remove any CSS tagging left inside a VTT file.
 *
 * Tags like <c.my-class>text</c> can be added to VTT files to restyle text,
 * and we want to remove these before using the text for presentation.
 *
 * Returns a parsed string composed of HTML and VTT content.
 *
 * @function parseVTTContent
 * @param {Player} player
 *       A Video.js player.
 * @param {string} chosenTrack
 *       The track to use as transcript content.
 * @return {Array} List of <button> elements
 */
const parseVTTContent = (player, chosenTrack) => {
  const buttonList = [];

  // extract cues from the cue list and build buttons to append to the div
  for (let i = 0; i < chosenTrack.cues.length; i++) {
    const cue = chosenTrack.cues[i];
    const contentButton = global.document.createElement('button');
    let cueTime = cue.startTime;
    let parsedCue = '';

    if (cue.text) {
      // timestamp should be in the form of MM:SS
      // cueTime represents a seconds value
      cueTime = parseInt(cueTime, 10);
      if (cueTime <= 59) {
        if (cueTime < 10) {
          cueTime = `0:0${cueTime}`;
        } else {
          cueTime = `0:${cueTime}`;
        }
      } else {
        const minutes = parseInt(cueTime / 60, 10);
        let seconds = parseInt(cueTime % 60, 10);

        if (seconds < 10) {
          seconds = `0${seconds}`;
        }

        cueTime = `${minutes}:${seconds}`;
      }

      parsedCue += `<span class="vjs-ts-cue-time">${cueTime}</span> `;

      parsedCue += parseCueContent(cue);

      contentButton.className = 'vjs-ts-content-button';
      contentButton.setAttribute('type', 'button');
      contentButton.onclick = function(event) {
        const newActive = event.target;
        const oldActive = global.document.querySelector('button[aria-current]');

        if (oldActive) {
          oldActive.removeAttribute('aria-current');
        }
        newActive.setAttribute('aria-current', 'true');

        // jump to time
        // because this is outside the widget, we have to parse the label to
        // figure out what time to jump to
        const labelContent = newActive.getAttribute('aria-label');
        const timeReObj = new RegExp('(?:Jump to )(\\d{1,2}:\\d{2})', 'g');
        const results = timeReObj.exec(labelContent);
        const times = results[1].split(':');

        times[0] = times[0] * 60;
        player.currentTime(times[0] + times[1]);
      };
      contentButton.setAttribute('aria-label', `Jump to ${cueTime}`);
      contentButton.innerHTML = parsedCue;

      buttonList.push(contentButton);
    }
  }

  return buttonList;
};

/**
 * Compiles the widget container and appends it to the page.
 *
 * @function createWidget
 * @param {string} widgetContent
 *       An HTML string that will be added to the container as its inner HTML.
 * @param {Element} appendContainer
 *       The HTML node that represents the container the widget will be appended to.
 * @param {Element} appendButton
 *       The HTML node that represents the container the "show transcript" button will be
 *       appended to.
 * @param {boolean} hideTranscript
 *       Represents whether or not the transcript widget will be visible once appended.
 */
const createWidget = (widgetContent, appendContainer, appendButton, hideTranscript) => {
  if (appendContainer) {
    const containerDiv = global.document.createElement('div');

    containerDiv.id = 'vjs-transcript';
    containerDiv.className = 'vjs-transcript-widget';
    containerDiv.setAttribute('tabindex', '-1');
    containerDiv.setAttribute('role', 'complementary');
    containerDiv.setAttribute('aria-label', 'interactive transcript');

    if (typeof (widgetContent) !== 'string') {
      const closeDivButton = global.document.createElement('button');
      const viewTranscriptButton = global.document.createElement('button');

      // Create external "show transcript" button
      viewTranscriptButton.id = 'vjs-transcript-show';
      viewTranscriptButton.innerHTML = 'Video transcript';
      viewTranscriptButton.setAttribute('type', 'button');
      viewTranscriptButton.className = 'vjs-ts-show-button';
      viewTranscriptButton.onclick = function(event) {
        // move from hidden to visible
        const container = global.document.getElementById('vjs-transcript');
        const viewButton = global.document.getElementById('vjs-transcript-show');

        if (container.className.includes('ts-visible')) {
          container.className = 'vjs-transcript-widget ts-hidden';
          viewButton.innerText = 'Video transcript';
          viewButton.classList.remove('ts-active');
          viewButton.focus();
        } else {
          container.className = 'vjs-transcript-widget ts-visible';
          viewButton.innerText = 'Close transcript';
          viewButton.classList.add('ts-active');
          container.focus();
        }
      };

      // Because this button lives outside the widget, append it first
      // Needs it's own container due to flexbox
      const viewContainer = global.document.createElement('div');

      viewContainer.className = 'vjs-ts-view-container';
      viewContainer.appendChild(viewTranscriptButton);
      appendButton.appendChild(viewContainer);

      // Create internal close button for the widget
      closeDivButton.innerHTML = 'Close video transcript';
      closeDivButton.setAttribute('type', 'button');
      closeDivButton.setAttribute('class', 'vjs-ts-close-button');
      closeDivButton.onclick = function(event) {
        // move from visible to hidden
        const container = global.document.getElementById('vjs-transcript');
        const viewButton = global.document.getElementById('vjs-transcript-show');

        container.className = 'vjs-transcript-widget ts-hidden';
        viewButton.innerText = 'Video transcript';
        viewButton.focus();
      };

      // This also needs it's own container for flexbox
      const closeContainer = global.document.createElement('div');
      const tsHeader = global.document.createElement('p');

      tsHeader.innerHTML = 'Video transcript';
      tsHeader.className = 'vjs-ts-header';
      closeContainer.className = 'vjs-ts-close-container';
      closeContainer.appendChild(tsHeader);
      closeContainer.appendChild(closeDivButton);
      containerDiv.appendChild(closeContainer);

      // Create container for buttons; append buttons one by one
      const contentContainer = global.document.createElement('div');

      contentContainer.className = 'vjs-ts-content-container';
      widgetContent.forEach(function(value) {
        contentContainer.appendChild(value);
      });
      containerDiv.appendChild(contentContainer);

      // append container to the player
      if (hideTranscript) {
        containerDiv.className += ' ts-hidden';
        viewTranscriptButton.className += ' ts-visible';
      } else {
        containerDiv.className += ' ts-visible';
        viewTranscriptButton.className += ' ts-hidden';
      }
    } else {
      const widgetNode = global.document.createElement('span');

      widgetNode.innerHTML = widgetContent;
      containerDiv.appendChild(widgetNode);
    }

    appendContainer.appendChild(containerDiv);
  }
};

/**
 * Function to check on cue status.
 *
 * @function checkCueStatus
 * @param {Object} textTrack
 *       A TextTrack object.
 * @return {boolean} true if cues are generated, else false
 */
const checkCueStatus = (textTrack) => {
  if (textTrack.cues && textTrack.cues.length) {
    return true;
  }

  return false;
};

// Cross-compatibility for Video.js 5 and 6.
const registerPlugin = videojs.registerPlugin || videojs.plugin;
// const dom = videojs.dom || videojs;

/**
 * Function invoked on player ready.  Sets up most of the helper functions.
 *
 * @function onPlayerReady
 * @param  {Player} player
 *       A Video.js player.
 * @param  {Object} [options={}]
 *       An object of options left to the plugin author to define.
 */
const onPlayerReady = (player, options) => {
  const defaultTextTrack = determineDefaultTrack(player, options.trackKind);
  const appendContainer = global.document.getElementById(options.appendContainer);
  const appendButton = global.document.getElementById(options.appendButton);
  let widgetContent;

  if (defaultTextTrack) {
    if (checkCueStatus(defaultTextTrack)) {
      widgetContent = parseVTTContent(player, defaultTextTrack);
      createWidget(widgetContent, appendContainer, appendButton, options.hideTranscript);
    }
  } else {
    widgetContent = '<p>Transcript currently unavailable</p>';
    createWidget(widgetContent, appendContainer, appendButton, options.hideTranscript);
  }

};

/**
 * Function to track and update the active line in the transcript. Runs on cuechange.
 *
 * @function scrollActiveLine
 * @param {Track} track
 *       The current active track.
 * @param {int} offset
 *       Pixel offset used to set the correct scroll for each cue.  See description
 *       under defaults.
 */
const scrollActiveLine = (track, offset) => {
  const transcriptContent = global.document.querySelector('.vjs-ts-content-container');
  const transcriptButtons = global.document.getElementsByClassName('vjs-ts-content-button');
  const activeButton = global.document.querySelector('.vjs-ts-content-button[aria-current="true"]');
  const cueButtonRe = '(?:\\d{1,2}:\\d{2} ?)(.+)';
  const cueButtonReObj = new RegExp(cueButtonRe, '');
  const cue = track.activeCues[0];

  if (typeof (cue) !== 'undefined') {
    const cueText = parseCueContent(cue).trim();

    // Check if a widget cue is already highlighted - if so, move to the next one
    // Else find the one that matches the current cue
    if (activeButton) {
      // Compare the text content of the active button to the active cue
      const activeResults = cueButtonReObj.exec(activeButton.innerText.replace('\n', ' '));

      if (activeResults && !cueText.includes(activeResults[1])) {
        // Current button isn't current, check next button
        const siblingButton = activeButton.nextSibling;

        if (siblingButton) {
          if (siblingButton.innerText.includes(cueText)) {
            activeButton.removeAttribute('aria-current');
            siblingButton.setAttribute('aria-current', 'true');

            // Should container scroll
            if (transcriptContent.offsetHeight < transcriptContent.scrollHeight) {
              // Scroll the content to the active cue
              const containerOffset = siblingButton.offsetTop;

              transcriptContent.scrollTop = containerOffset - offset;
            }
          }
        }
      }
    } else {
      // Compare active cue to list in order and assign active accordingly
      for (let i = 0; i < transcriptButtons.length; i++) {
        const currentButton = transcriptButtons[i];
        const currentResults = cueButtonReObj.exec(currentButton.innerText.replace('\n', ' '));

        if (currentResults && cueText.includes(currentResults[1])) {
          currentButton.setAttribute('aria-current', 'true');
        }
      }
    }
  }
};

/**
 * A video.js plugin.
 *
 * In the plugin function, the value of `this` is a video.js `Player`
 * instance. You cannot rely on the player being in a "ready" state here,
 * depending on how the plugin is invoked. This may or may not be important
 * to you; if not, remove the wait for "ready"!
 *
 * @function transcriptWidget
 * @param  {Object} [options={}]
 *       An object of options left to the plugin author to define.
 */
const transcriptWidget = function(options) {
  this.ready(() => {
    const mergedOptions = videojs.mergeOptions(defaults, options);
    const track = determineDefaultTrack(this, mergedOptions.trackKind);

    if (track) {
      try {
        track.on('cuechange', function() {
          scrollActiveLine(this, mergedOptions.offset);
        });

        // Also hitch this, will fix highlight on initial view
        track.on('modechange', function() {
          scrollActiveLine(this, mergedOptions.offset);
        });
      } catch (e) {
        track.oncuechange = function() {
          scrollActiveLine(this, mergedOptions.offset);
        };
      }
    }

    onPlayerReady(this, mergedOptions);
  });
};

// Register the plugin with video.js.
registerPlugin('transcriptWidget', transcriptWidget);

// Include the version number.
transcriptWidget.VERSION = '__VERSION__';

module.exports = transcriptWidget;
